import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Shared Proctor Controller Hook
 * Provides browser-based proctoring features:
 * - Tab switching / window-blur detection
 * - Copy/paste blocking + large-paste / programmatic-input anomaly (Phase 7.8)
 * - Camera + Microphone monitoring
 * - Audio level + VAD + server-side audio-clip analysis (Phase 7.7)
 * - Anomaly logging
 *
 * Initialization is one-shot per mount: the setup effect ignores its callbacks in
 * the dep list by pinning them to refs. Prevents duplicate visibilitychange
 * listeners (→ double-fire) and repeated getUserMedia retry loops (→ backend spam)
 * caused by parent re-renders that re-create the `onAnomalyDetected` prop.
 *
 * @param {Object}  opts
 * @param {boolean} [opts.enabled=true]              — master kill-switch
 * @param {(a:Object)=>void} [opts.onAnomalyDetected] — fired for every anomaly
 * @param {string|null}      [opts.proctorSessionId]  — if set, periodic audio
 *          clips are POSTed to `/proctoring/{id}/audio-clip` for server-side
 *          Whisper + LLM judge. When omitted only browser-side VAD events fire.
 */
export function useProctorController({
  enabled = true,
  onAnomalyDetected,
  proctorSessionId = null,
}) {
  const [cameraActive, setCameraActive] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [faceDetected, setFaceDetected] = useState(true);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [clipboardEventCount, setClipboardEventCount] = useState(0);
  const [anomalyLogs, setAnomalyLogs] = useState([]);
  const [audioLevel, setAudioLevel] = useState(0); // 0..1 RMS
  const [voiceActive, setVoiceActive] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const faceDetectorRef = useRef(null);
  const faceCheckIntervalRef = useRef(null);
  const faceNotVisibleTimerRef = useRef(null);

  // Web Audio API
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const audioTickIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingActiveRef = useRef(false);
  const voiceActiveStartRef = useRef(null);
  const lastClipUploadRef = useRef(0);

  // Keep parent-supplied callback + enabled flag in refs so our setup functions
  // never need to list them in dep arrays → they stay referentially stable.
  const onAnomalyRef = useRef(onAnomalyDetected);
  const enabledRef = useRef(enabled);
  const proctorSessionIdRef = useRef(proctorSessionId);
  useEffect(() => {
    onAnomalyRef.current = onAnomalyDetected;
  }, [onAnomalyDetected]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  useEffect(() => {
    proctorSessionIdRef.current = proctorSessionId;
  }, [proctorSessionId]);

  // Log anomaly helper — stable identity, reads latest callback via ref.
  const logAnomaly = useCallback((eventType, details, severity = "medium", extra = {}) => {
    const anomaly = {
      event_type: eventType,
      details,
      severity,
      timestamp: new Date().toISOString(),
      ...extra,
    };
    setAnomalyLogs(prev => [...prev, anomaly]);
    if (onAnomalyRef.current) {
      onAnomalyRef.current(anomaly);
    }
    return anomaly;
  }, []);

  // ========== TAB SWITCHING + WINDOW BLUR DETECTION ==========
  const setupTabSwitchDetection = useCallback(() => {
    let lastFireTs = 0;
    const DEBOUNCE_MS = 150;

    const fireSwitch = (reason) => {
      const now = Date.now();
      if (now - lastFireTs < DEBOUNCE_MS) return;
      lastFireTs = now;
      if (!enabledRef.current) return;
      setTabSwitchCount(prev => {
        const newCount = prev + 1;
        logAnomaly(
          reason === "blur" ? "WINDOW_BLUR_DETECTED" : "TAB_SWITCH_DETECTED",
          reason === "blur"
            ? `Window lost focus (event #${newCount}) — Alt-Tab or app switch`
            : `Tab hidden (event #${newCount})`,
          "high",
        );
        if (newCount >= 2) {
          toast.warning(
            "Switching away from the test is being recorded. Please stay focused.",
            { duration: 5000 },
          );
        }
        return newCount;
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) fireSwitch("visibility");
    };
    const handleBlur = () => fireSwitch("blur");

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [logAnomaly]);

  // ========== CLIPBOARD BLOCKING + LARGE-PASTE DETECTION ==========
  // Phase 7.8: distinguish small pastes from large / programmatic pastes.
  const setupClipboardBlocking = useCallback(() => {
    const handleCopy = (e) => {
      if (enabledRef.current) {
        e.preventDefault();
        setClipboardEventCount(prev => prev + 1);
        logAnomaly("CLIPBOARD_COPY_DETECTED", "Copy attempt blocked", "low");
        toast.warning("Copy action is disabled during this assessment");
      }
    };

    const handleCut = (e) => {
      if (enabledRef.current) {
        e.preventDefault();
        setClipboardEventCount(prev => prev + 1);
        logAnomaly("CLIPBOARD_CUT_DETECTED", "Cut attempt blocked", "low");
        toast.warning("Cut action is disabled during this assessment");
      }
    };

    const handlePaste = (e) => {
      if (!enabledRef.current) return;
      setClipboardEventCount(prev => prev + 1);
      let pastedLen = 0;
      try {
        const txt = (e.clipboardData || window.clipboardData)?.getData("text") || "";
        pastedLen = txt.length;
      } catch {
        /* some browsers deny clipboardData read — treat as unknown */
      }
      if (pastedLen >= 150) {
        logAnomaly(
          "PASTE_LARGE_TEXT",
          `Large paste intercepted (${pastedLen} chars) — likely external answer source`,
          "high",
          { paste_length: pastedLen },
        );
        toast.warning("Large paste detected and recorded.", { duration: 4000 });
      } else {
        logAnomaly(
          "CLIPBOARD_PASTE_DETECTED",
          `Paste attempt detected (${pastedLen || "unknown"} chars)`,
          "medium",
          { paste_length: pastedLen },
        );
      }
    };

    const handleContextMenu = (e) => {
      if (enabledRef.current) {
        e.preventDefault();
        toast.warning("Right-click is disabled during this assessment", { duration: 2000 });
      }
    };

    document.addEventListener("copy", handleCopy);
    document.addEventListener("cut", handleCut);
    document.addEventListener("paste", handlePaste);
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("cut", handleCut);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [logAnomaly]);

  // ========== KEYSTROKE CADENCE / PROGRAMMATIC-INPUT DETECTION ==========
  // Phase 7.8: track input events vs keydown events — a burst of text that
  // arrives via `input` without a matching keydown stream is likely JS-injected
  // (browser-extension / devtools helper). Threshold chosen to avoid false
  // positives from IME composition or mobile autocorrect.
  const setupKeystrokeAnomaly = useCallback(() => {
    let recentKeydownTs = [];
    const KEYDOWN_WINDOW_MS = 400;

    const handleKeydown = () => {
      if (!enabledRef.current) return;
      const now = Date.now();
      recentKeydownTs.push(now);
      // keep last 20 only
      if (recentKeydownTs.length > 20) recentKeydownTs = recentKeydownTs.slice(-20);
    };

    const handleInput = (ev) => {
      if (!enabledRef.current) return;
      // Only care about InputEvents with insertion > small threshold
      const delta = (ev.data || "").length;
      if (delta < 30) return;
      const now = Date.now();
      const matching = recentKeydownTs.filter(ts => now - ts < KEYDOWN_WINDOW_MS).length;
      // If we just inserted 30+ chars but saw ≤2 keydowns in the last 400 ms,
      // either the user pasted (handled above) or it was programmatic.
      if (matching <= 2) {
        logAnomaly(
          "PROGRAMMATIC_INPUT",
          `Text inserted without matching keystrokes (${delta} chars, ${matching} keydown events)`,
          "critical",
          { insert_length: delta, keydown_count: matching },
        );
      }
    };

    document.addEventListener("keydown", handleKeydown, true);
    document.addEventListener("input", handleInput, true);
    return () => {
      document.removeEventListener("keydown", handleKeydown, true);
      document.removeEventListener("input", handleInput, true);
    };
  }, [logAnomaly]);

  // ========== CAMERA + MIC SETUP ==========
  const setupMediaDevices = useCallback(async () => {
    if (!enabledRef.current) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 320, height: 240 },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];

      setCameraActive(videoTrack && videoTrack.enabled);
      setMicActive(audioTrack && audioTrack.enabled);

      if (videoTrack) {
        videoTrack.onended = () => {
          setCameraActive(false);
          logAnomaly("CAMERA_STREAM_ENDED", "Camera was turned off or disconnected", "high");
          toast.error("Your camera has been turned off. Please enable it to continue.");
        };
      }

      if (audioTrack) {
        audioTrack.onended = () => {
          setMicActive(false);
          logAnomaly("MIC_STREAM_ENDED", "Microphone was turned off or disconnected", "high");
          toast.error("Your microphone has been turned off. Please enable it to continue.");
        };
      }

      if (window.FaceDetector) {
        try {
          faceDetectorRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 5 });
          startFaceDetection();
        } catch (e) {
          console.warn("FaceDetector not available", e);
        }
      }

      // Kick off audio analyser once we have an audio track.
      if (audioTrack) {
        setupAudioAnalyser(stream);
      }

    } catch (e) {
      console.error("Media devices error:", e);
      setCameraError(e.message || "Camera/microphone access denied");
      setCameraActive(false);
      setMicActive(false);
      logAnomaly("MEDIA_ACCESS_DENIED", `Camera/mic access error: ${e.message}`, "high");
      toast.error("Camera and microphone access is required for this assessment. Please grant permissions and refresh.");
    }
  }, [logAnomaly]);

  // ========== AUDIO ANALYSER + VAD + CLIP UPLOAD ==========
  // Continuous RMS sampling; on sustained voice (>= VOICE_SUSTAIN_MS) we fire
  // a SUSTAINED_SPEECH anomaly and (when proctorSessionId is set) capture a
  // ~8s MediaRecorder clip and POST it for server-side Whisper+LLM judging.
  const setupAudioAnalyser = useCallback((stream) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      analyserRef.current = analyser;

      const buf = new Uint8Array(analyser.fftSize);
      const VOICE_THRESHOLD = 0.08; // normalized RMS; tuned for typical laptop mic
      const SPIKE_THRESHOLD = 0.35;
      const VOICE_SUSTAIN_MS = 2000;
      const CLIP_COOLDOWN_MS = 20000; // avoid spamming server every 2s
      let lastSpikeTs = 0;

      audioTickIntervalRef.current = setInterval(() => {
        if (!analyserRef.current || !enabledRef.current) return;
        analyser.getByteTimeDomainData(buf);
        // RMS
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setAudioLevel(rms);

        // Volume spike
        if (rms > SPIKE_THRESHOLD && Date.now() - lastSpikeTs > 3000) {
          lastSpikeTs = Date.now();
          logAnomaly(
            "AUDIO_VOLUME_SPIKE",
            `Audio volume spike (level ${rms.toFixed(2)})`,
            "low",
            { level: +rms.toFixed(3) },
          );
        }

        // VAD state machine
        const isVoice = rms > VOICE_THRESHOLD;
        if (isVoice && !voiceActiveStartRef.current) {
          voiceActiveStartRef.current = Date.now();
          setVoiceActive(true);
        } else if (!isVoice && voiceActiveStartRef.current) {
          voiceActiveStartRef.current = null;
          setVoiceActive(false);
        }

        if (
          voiceActiveStartRef.current &&
          Date.now() - voiceActiveStartRef.current > VOICE_SUSTAIN_MS &&
          Date.now() - lastClipUploadRef.current > CLIP_COOLDOWN_MS &&
          !recordingActiveRef.current
        ) {
          // Reset VAD start so we don't double-fire while recording.
          voiceActiveStartRef.current = null;
          logAnomaly(
            "SUSTAINED_SPEECH",
            "Sustained speech detected — uploading audio clip for review",
            "medium",
          );
          if (proctorSessionIdRef.current && stream.getAudioTracks().length) {
            captureAndUploadAudioClip(stream);
          }
        }
      }, 200);
    } catch (e) {
      console.warn("Audio analyser setup failed:", e);
    }
  }, [logAnomaly]);

  const captureAndUploadAudioClip = useCallback((stream) => {
    if (recordingActiveRef.current) return;
    try {
      // Record only the audio track (drops video to keep clip small).
      const audioOnly = new MediaStream(stream.getAudioTracks());
      let mimeType = "audio/webm;codecs=opus";
      if (typeof MediaRecorder !== "undefined" && !MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = "";
      }
      const recorder = mimeType
        ? new MediaRecorder(audioOnly, { mimeType })
        : new MediaRecorder(audioOnly);
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];
      recordingActiveRef.current = true;
      lastClipUploadRef.current = Date.now();

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size) recordingChunksRef.current.push(ev.data);
      };
      recorder.onstop = async () => {
        recordingActiveRef.current = false;
        const sessionId = proctorSessionIdRef.current;
        const blob = new Blob(recordingChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        recordingChunksRef.current = [];
        if (!sessionId || blob.size < 500) return;
        try {
          const form = new FormData();
          form.append("clip", blob, `clip-${Date.now()}.webm`);
          form.append("reason", "sustained_speech");
          await api.post(`/proctoring/${sessionId}/audio-clip`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          console.warn("Audio clip upload failed:", err?.message || err);
        }
      };

      recorder.start();
      setTimeout(() => {
        if (recorder.state === "recording") recorder.stop();
      }, 8000);
    } catch (e) {
      recordingActiveRef.current = false;
      console.warn("Audio recorder error:", e);
    }
  }, []);

  // ========== FACE DETECTION ==========
  const startFaceDetection = useCallback(() => {
    if (!faceDetectorRef.current || !videoRef.current || !enabledRef.current) return;

    faceCheckIntervalRef.current = setInterval(async () => {
      try {
        if (!videoRef.current || videoRef.current.readyState < 2) return;

        const faces = await faceDetectorRef.current.detect(videoRef.current);

        if (faces.length === 0) {
          setFaceDetected(false);
          if (!faceNotVisibleTimerRef.current) {
            faceNotVisibleTimerRef.current = setTimeout(() => {
              logAnomaly("FACE_NOT_VISIBLE", "Face not visible for >5 seconds", "medium");
              toast.warning("Your face is not visible. Please ensure the camera can see you.", {
                duration: 4000,
              });
              faceNotVisibleTimerRef.current = null;
            }, 5000);
          }
        } else {
          setFaceDetected(true);
          clearTimeout(faceNotVisibleTimerRef.current);
          faceNotVisibleTimerRef.current = null;

          if (faces.length > 1) {
            logAnomaly("MULTIPLE_FACES_DETECTED", `${faces.length} faces detected`, "high");
            toast.warning("Multiple faces detected. Please ensure you are completing this assessment independently.", {
              duration: 5000,
            });
          }
        }
      } catch (e) {
        console.warn("ProctorController: face detection tick failed", e);
      }
    }, 3000);
  }, [logAnomaly]);

  // ========== INITIALIZATION (one-shot per mount) ==========
  useEffect(() => {
    if (!enabled) return undefined;

    const cleanupTabSwitch = setupTabSwitchDetection();
    const cleanupClipboard = setupClipboardBlocking();
    const cleanupKeystroke = setupKeystrokeAnomaly();
    setupMediaDevices();

    return () => {
      cleanupTabSwitch();
      cleanupClipboard();
      cleanupKeystroke();
      clearInterval(faceCheckIntervalRef.current);
      clearTimeout(faceNotVisibleTimerRef.current);
      clearInterval(audioTickIntervalRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
      }
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch { /* ignore */ }
      }

      // Stop media tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
    // Intentionally omit the setup callbacks from deps — they are referentially
    // stable (their identities are pinned via `logAnomaly`'s empty-dep useCallback).
    // Listing them causes listener-duplication and getUserMedia retry loops when
    // the parent re-renders without memoizing `onAnomalyDetected`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    // State
    cameraActive,
    micActive,
    cameraError,
    faceDetected,
    tabSwitchCount,
    clipboardEventCount,
    anomalyLogs,
    audioLevel,
    voiceActive,

    // Refs
    videoRef,

    // Actions
    logAnomaly,
  };
}
